import { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { userService, type TemporaryPassword, type User } from '@/services/userService'

export interface ResetPasswordModalRef {
  open: (user: User) => void
}

/**
 * Asks before resetting, then shows the temporary password an owner hands over.
 *
 * The person's own password keeps working unless the owner ticks the box to
 * revoke it, for a password somebody else may know; then it stops at once and
 * their sessions end. Either way nothing is sent until the owner confirms.
 *
 * Displayed once and stored nowhere, which is why the modal says so out loud:
 * an owner who closes this without copying it has to reset again, and that is
 * the correct behaviour rather than a reason to keep it somewhere.
 */
const ResetPasswordModal = forwardRef<ResetPasswordModalRef>((_props, ref) => {
  const { t } = useTranslation(['passwordReset', 'users'])
  const [isOpen, setIsOpen] = useState(false)
  const [revokeCurrent, setRevokeCurrent] = useState(false)
  const [target, setTarget] = useState<User | null>(null)
  const [result, setResult] = useState<TemporaryPassword | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  useImperativeHandle(ref, () => ({
    open: (user: User) => {
      setTarget(user)
      setResult(null)
      setError('')
      setCopied(false)
      setRevokeCurrent(false)
      setIsOpen(true)
    },
  }))

  const issue = async () => {
    if (!target || isLoading) return
    setIsLoading(true)
    setError('')
    try {
      setResult(await userService.resetPassword(target.id, revokeCurrent))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'passwordReset.owner.failed')
    } finally {
      setIsLoading(false)
    }
  }

  const copy = async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result.temporary_password)
      setCopied(true)
    } catch {
      // Clipboard access is refused in plenty of contexts. The code is on
      // screen either way, so there is nothing to recover from here.
    }
  }

  const close = () => {
    if (!isLoading) setIsOpen(false)
  }

  if (!isOpen || !target) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/30 backdrop-blur-md">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-blue-600">{t('owner.heading', 'Reset Password')}</h2>
              <p className="text-sm text-gray-600 mt-1">{t('owner.subtitle', 'Issue a temporary password')}</p>
            </div>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isLoading}
              aria-label={t('owner.close', 'Close')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" role="alert">
              <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-700">{t(error, 'The password could not be reset.')}</p>
            </div>
          )}

          {result ? (
            <>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900 mb-1">
                      {t('owner.modalTitle', 'Temporary password for {{account}}', { account: target.account })}
                    </p>
                    <p className="text-xs text-green-700">
                      {t(
                        'owner.explain',
                        'Give this to the person by a route you trust. Signing in with it takes them straight to choosing a new password, so you will not know the one they end up with.'
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <code className="block text-center text-lg font-mono tracking-wider text-gray-900 break-all">
                  {result.temporary_password}
                </code>
                {result.expires_at && (
                  <p className="mt-2 text-center text-xs text-gray-600">
                    {t('owner.validUntil', 'Valid until {{time}}, for one sign-in.', {
                      time: new Date(result.expires_at).toLocaleString(),
                    })}
                  </p>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                  <li>{t('owner.onceOnly', 'This is the only time it is shown. If it is lost, reset again.')}</li>
                  <li>
                    {result.revoked_current
                      ? t('owner.currentRevoked', 'Their current password no longer works and they have been signed out everywhere.')
                      : t('owner.currentKept', 'Their current password still works. Both can be used until the temporary one expires or has signed in once.')}
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  {t('owner.confirmIntro', 'Reset the password for the following user:')}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{t('users:table.account', 'Account')}:</span>
                    <span className="text-sm text-gray-900">{target.account}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{t('users:table.name', 'Name')}:</span>
                    <span className="text-sm text-gray-900">{target.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{t('users:table.role', 'Role')}:</span>
                    <span className="text-sm text-gray-900">{t(`users:roles.${target.platform_role}`, target.platform_role)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">{t('owner.howItWorks', 'How it works')}</h4>
                    <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                      <li>{t('owner.point1', 'Their current password keeps working.')}</li>
                      <li>{t('owner.point2', 'The temporary password works for 24 hours and for one sign-in.')}</li>
                      <li>{t('owner.point3', 'Signing in with it takes them straight to choosing a new password.')}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <label
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  revokeCurrent ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={revokeCurrent}
                  onChange={(e) => setRevokeCurrent(e.target.checked)}
                  disabled={isLoading}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <span className="text-sm">
                  <span className="block font-medium text-gray-900">
                    {t('owner.revokeLabel', 'Also revoke their current password and sign them out everywhere')}
                  </span>
                  <span className="block mt-0.5 text-gray-600">
                    {t('owner.revokeHint', 'Tick this only if the password may be known to someone else.')}
                  </span>
                </span>
              </label>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
          {result ? (
            <>
              <button
                type="button"
                onClick={copy}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {copied ? t('owner.copied', 'Copied') : t('owner.copy', 'Copy password')}
              </button>
              <button
                type="button"
                onClick={close}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('owner.close', 'Close')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isLoading}
              >
                {t('owner.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={() => void issue()}
                className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                  revokeCurrent ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
                disabled={isLoading}
              >
                {isLoading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{t('owner.resetPassword', 'Reset password')}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
})

ResetPasswordModal.displayName = 'ResetPasswordModal'
export default ResetPasswordModal
